import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "./api";

export const useVehicles = (memberId: string, includeDeactivated = false) =>
  useQuery({
    queryKey: ["portal", "member-vehicles", memberId, includeDeactivated],
    queryFn: () => api.listVehicles(memberId, includeDeactivated),
    enabled: Boolean(memberId),
  });

export const useVehicleCatalog = (
  catalog: api.VehicleCatalog | api.MaintenanceCatalog,
) =>
  useQuery({
    queryKey: ["portal", "catalog-entries", catalog],
    queryFn: () => api.listVehicleCatalog(catalog),
  });

export const useMaintenances = (vehicleId: string) =>
  useQuery({
    queryKey: ["portal", "vehicle-maintenances", vehicleId],
    queryFn: () => api.listMaintenances(vehicleId),
    enabled: Boolean(vehicleId),
  });

export function useVehicleMutations(memberId: string) {
  const client = useQueryClient();
  const invalidate = () => {
    void client.invalidateQueries({
      queryKey: ["portal", "member-vehicles", memberId],
    });
    // El conteo de vehículos activos vive en MemberSummary/Member (SPEC-202):
    // una baja o un alta lo desactualiza si no se invalida también acá.
    void client.invalidateQueries({ queryKey: ["portal", "member", memberId] });
    void client.invalidateQueries({ queryKey: ["portal", "members"] });
  };
  return {
    create: useMutation({
      mutationFn: (input: api.VehicleInput) =>
        api.createVehicle(memberId, input),
      onSuccess: invalidate,
    }),
    edit: useMutation({
      mutationFn: ({ id, input }: { id: string; input: api.VehicleInput }) =>
        api.editVehicle(id, input),
      onSuccess: invalidate,
    }),
    deactivate: useMutation({
      mutationFn: ({
        id,
        input,
      }: {
        id: string;
        input: { deactivationReasonId: string; confirmed: boolean };
      }) => api.deactivateVehicle(id, input),
      onSuccess: invalidate,
    }),
  };
}

// SPEC-203 §4 y el prototipo solo dibujan alta de mantenimiento — no hay
// enlace de edición por registro (MaintenancePanel.tsx/.test.tsx). Por eso
// esta mutación no expone `edit`: nada en el portal llama a un PUT de
// mantenimiento hoy.
export function useMaintenanceMutations(vehicleId: string) {
  const client = useQueryClient();
  const invalidate = () =>
    client.invalidateQueries({
      queryKey: ["portal", "vehicle-maintenances", vehicleId],
    });
  return {
    create: useMutation({
      mutationFn: (input: api.MaintenanceInput) =>
        api.createMaintenance(vehicleId, input),
      onSuccess: invalidate,
    }),
  };
}
